import axios from "axios";
import { getToken, setToken, getRefreshToken, setRefreshToken, clearAuth } from "../lib/tokenStore";

const BASE_URL = import.meta.env.VITE_BASE_API_URL;

const authHttp = axios.create({
  baseURL: BASE_URL,
});

// Attach access token to every request
authHttp.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 — refresh and retry
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

authHttp.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    const errorCode = error.response?.data?.code;

    // Only handle 401s
    if (status !== 401) {
      return Promise.reject(error);
    }

    // If this is a hard rejection (user not allowed), don't try to refresh
    if (errorCode === "USER_NOT_ALLOWED") {
      clearAuth();
      window.location.href = "/workout";
      return Promise.reject(error);
    }

    // Don't retry if we already retried this request
    if (originalRequest._retry) {
      clearAuth();
      window.location.href = "/workout";
      return Promise.reject(error);
    }

    // Attempt refresh for any 401 (TOKEN_EXPIRED, INVALID_TOKEN, or generic)
    if (isRefreshing) {
      // Queue requests while refreshing
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => {
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return authHttp(originalRequest);
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      isRefreshing = false;
      clearAuth();
      window.location.href = "/workout";
      return Promise.reject(error);
    }

    try {
      const response = await axios.post(`${BASE_URL}auth/end-user/token/refresh`, {
        refreshToken,
      });

      // Handle both response shapes: { data: { accessToken, refreshToken } } or { accessToken, refreshToken }
      const payload = response.data?.data || response.data;
      const newAccessToken = payload.accessToken;
      const newRefreshToken = payload.refreshToken;

      if (!newAccessToken) {
        throw new Error("No access token in refresh response");
      }

      setToken(newAccessToken);
      if (newRefreshToken) {
        setRefreshToken(newRefreshToken);
      }

      processQueue(null, newAccessToken);

      originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
      return authHttp(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      clearAuth();
      window.location.href = "/workout";
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

export default authHttp;
